import { Glyphs, WidgetSelectDropdownOption } from '../../../../rapid-ui'
import { MetaOverlayConfiguration } from '../../../../views'

export function getFillOptions(t: (key: string) => string): Array<WidgetSelectDropdownOption<'none' | 'flat'>> {
	return [
		{
			id: 'none',
			label: (
				<div className="flex items-center gap-2">
					<div className="size-4 ring ring-black/15 rounded" />
					{t('editor.overlay.noOverlay')}
				</div>
			),
		},
		{
			id: 'flat',
			label: (
				<div className="flex items-center gap-2">
					<div className="size-4 ring ring-black/15 bg-black/5 rounded" />
					{t('editor.overlay.flatColor')}
				</div>
			),
		},
	]
}

function BlendModeIcon({ blendMode }: { blendMode: MetaOverlayConfiguration['fillBlendMode'] }) {
	return (
		<div className="size-4 relative isolate">
			<div
				className="size-3 rounded-full bg-red-500 absolute top-[-1px] left-[-1px]"
				style={{ mixBlendMode: blendMode }}
			/>
			<div
				className="size-3 rounded-full bg-green-500 absolute top-[-1px] right-[-1px]"
				style={{ mixBlendMode: blendMode }}
			/>
			<div
				className="size-3 rounded-full bg-blue-500 absolute bottom-[-1px] left-1/2 -translate-x-1/2"
				style={{ mixBlendMode: blendMode }}
			/>
		</div>
	)
}

export const fillBlendModeOptions: Array<WidgetSelectDropdownOption<MetaOverlayConfiguration['fillBlendMode']>> = [
	{
		id: 'normal',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="normal" />
				Normal
			</div>
		),
	},
	{
		id: 'multiply',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="multiply" />
				Multiply
			</div>
		),
	},
	{
		id: 'screen',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="screen" />
				Screen
			</div>
		),
	},
	{
		id: 'overlay',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="overlay" />
				Overlay
			</div>
		),
	},
	{
		id: 'darken',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="darken" />
				Darken
			</div>
		),
	},
	{
		id: 'lighten',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="lighten" />
				Lighten
			</div>
		),
	},
	{
		id: 'color-dodge',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="color-dodge" />
				Color Dodge
			</div>
		),
	},
	{
		id: 'color-burn',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="color-burn" />
				Color Burn
			</div>
		),
	},
	{
		id: 'hard-light',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="hard-light" />
				Hard Light
			</div>
		),
	},
	{
		id: 'soft-light',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="soft-light" />
				Soft Light
			</div>
		),
	},
	{
		id: 'difference',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="difference" />
				Difference
			</div>
		),
	},
	{
		id: 'exclusion',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="exclusion" />
				Exclusion
			</div>
		),
	},
	{
		id: 'hue',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="hue" />
				Hue
			</div>
		),
	},
	{
		id: 'saturation',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="saturation" />
				Saturation
			</div>
		),
	},
	{
		id: 'color',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="color" />
				Color
			</div>
		),
	},
	{
		id: 'luminosity',
		label: (
			<div className="flex items-center gap-2">
				<BlendModeIcon blendMode="luminosity" />
				Luminosity
			</div>
		),
	},
]

export function getMaskOptions(t: (key: string) => string): Array<WidgetSelectDropdownOption<'flat' | 'gradient'>> {
	return [
		{
			id: 'flat',
			label: (
				<div className="flex items-center gap-2">
					<div className="size-4 ring ring-black/15 bg-black/5 rounded" />
					{t('editor.overlay.flatMask')}
				</div>
			),
		},
		{
			id: 'gradient',
			label: (
				<div className="flex items-center gap-2">
					<div className="size-4 ring ring-black/15 bg-gradient-to-br from-black/0 to-black/20 rounded" />
					{t('editor.overlay.gradientMask')}
				</div>
			),
		},
	]
}

export const gradientOptions = [
	{
		id: 'to-r',
		label: (
			<div className="flex items-center gap-1.5">
				<div className="">
					<Glyphs.heroiconsMicro.RightArrowIcon />
				</div>
				<div>To Right</div>
			</div>
		),
	},
	{
		id: 'to-l',
		label: (
			<div className="flex items-center gap-1.5">
				<div className="rotate-180">
					<Glyphs.heroiconsMicro.RightArrowIcon />
				</div>
				<div>To Left</div>
			</div>
		),
	},
	{
		id: 'to-t',
		label: (
			<div className="flex items-center gap-1.5">
				<div className="-rotate-90">
					<Glyphs.heroiconsMicro.RightArrowIcon />
				</div>
				<div>To Top</div>
			</div>
		),
	},
	{
		id: 'to-b',
		label: (
			<div className="flex items-center gap-1.5">
				<div className="rotate-90">
					<Glyphs.heroiconsMicro.RightArrowIcon />
				</div>
				<div>To Bottom</div>
			</div>
		),
	},
] as const
